/**
 * ImageGenerator — Extracted image generation logic from FirebaseIntelligenceService.
 *
 * Handles Gemini 3 native image generation via `rawGenerateContent` with
 * IMAGE response modality. Uses the existing content pipeline (circuit
 * breakers, retry, etc.) through the IntelligenceContext.
 */

import type { Tool } from '@/shared/types/ai.dto';
import type { IntelligenceContext } from '../IntelligenceContext';
import type { GenerationConfig, GenerateImageOptions } from '@/shared/types/ai.dto';
import { INTELLIGENCE_CONFIG, INTELLIGENCE_MODELS } from '@/core/config/intelligence-models';

/**
 * Generate an image using the Gemini 3 native image generation model.
 *
 * This uses rawGenerateContent with IMAGE response modality — the model
 * returns inline image data in the response parts.
 */
export async function generateImage(
    ctx: IntelligenceContext,
    promptOrOptions: string | GenerateImageOptions,
    modelOverride?: string,
    configOverride?: GenerationConfig
): Promise<string> {
    return ctx.mediaBreaker.execute(async () => {
        await ctx.ensureInitialized();

        let prompt: string;
        let model = modelOverride || INTELLIGENCE_MODELS.IMAGE.GENERATION;
        let config = configOverride;

        if (typeof promptOrOptions === 'object' && 'prompt' in promptOrOptions) {
            prompt = promptOrOptions.prompt;
            model = promptOrOptions.model || model;
            config = promptOrOptions.config || config;
        } else {
            prompt = promptOrOptions as string;
        }

        // 1. Setup Tools (Enable Google Search for grounding by default, aka "Nano Banana Pro" logic)
        const tools: Tool[] = [{ googleSearch: {} }];

        // 2. Setup Config
        const generationConfig: GenerationConfig = {
            responseModalities: ['IMAGE'], // Specific to Gemini 3 Image
            mediaResolution: INTELLIGENCE_CONFIG.IMAGE.DEFAULT.mediaResolution as GenerationConfig['mediaResolution'],
            imageConfig: {
                aspectRatio: config?.aspectRatio || '1:1',
                imageSize: '4K', // "Perfect" quality
                personGeneration: config?.imageConfig?.personGeneration
            }
        };

        if (config?.numberOfImages) {
            generationConfig.candidateCount = config.numberOfImages as number;
        }

        const result = await ctx.rawGenerateContent(
            prompt,
            model,
            generationConfig,
            undefined,
            tools,
        );

        // 4. Extract Image
        // Gemini 3 returns images as inlineData in the parts
        const candidates = result.response.candidates;
        if (!candidates || candidates.length === 0) throw new Error('No candidates returned');

        const imagePart = candidates[0]!.content?.parts?.find(p => p.inlineData && p.inlineData.mimeType.startsWith('image/'));

        if (!imagePart || !imagePart.inlineData) {
            // Check if it was blocked or just text returned (e.g. "I cannot generate that")
            const textPart = candidates[0]!.content?.parts?.find(p => 'text' in p);
            if (textPart && 'text' in textPart) {
                throw new Error(`Generation blocked or failed: ${textPart.text}`);
            }
            throw new Error('No image data found in response');
        }

        return imagePart.inlineData.data;
    });
}
